import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { secondaryColor } from 'theme';

import i18n, { useTransl } from 'shared/lib/i18n';

import AsyncTableContainer from '../Table/AsyncTableContainer';

import {
  confirmToDeleteExternalAccount,
  loadExternalAccountsByType,
} from './actions';

import { generateTableColumns } from '../Table/utils';

import { TABLE_CONSTANTS } from '../Table/constants';

import {
  getExternalAccountListParams,
  getExternalAccountSchema,
  getExternalAccountsByType,
} from './selectors';

import { TableSkeleton } from '../Shared/skeletons';
import ListActions from '../Table/ListActions';

const generateColumns = (
  accountSchema,
  getRemoveAccountAction,
  openClearBrivoUsers,
) => {
  const accountColumns = generateTableColumns(accountSchema);

  if (accountSchema?.actions?.length) {
    const actionsColumn = {
      Header: i18n.t('title.actions'),
      id: 'actions',
      fixed: 'right',
      sortable: false,
      width: 120,
      accessor: (entry) => {
        const hasRemoveAction = Boolean(
          accountSchema?.actions?.find(({ type }) => type === 'remove'),
        );
        const removeAction = {
          label: i18n.t('actions.remove'),
          color: secondaryColor,
          show: hasRemoveAction,
          onClick: () => getRemoveAccountAction(entry?.id),
        };

        const hasBrivoCleanupAction = Boolean(
          accountSchema?.actions?.find(
            ({ type }) => type === 'clear_brivo_users',
          ),
        );
        const cleanBrivoUsersAction = {
          label: i18n.t('title.clearUsers'),
          show: hasBrivoCleanupAction,
          onClick: () => openClearBrivoUsers(entry?.id),
        };

        const actions = [cleanBrivoUsersAction, removeAction];

        return <ListActions actions={actions} isFullWidth />;
      },
    };

    accountColumns.push(actionsColumn);
  }

  return accountColumns || [];
};

export const ExternalAccountTable = () => {
  const { t } = useTransl();
  const { type } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const tableId = `${type}_external_accounts`;

  const accountSchema = useSelector(getExternalAccountSchema(type));
  const externalAccounts = useSelector(getExternalAccountsByType(type));
  const listParams = useSelector(getExternalAccountListParams(tableId));

  const getItems = (queryParams) => {
    loadExternalAccountsByType(dispatch, type, queryParams);
  };

  const getRemoveAccountAction = (accountId) =>
    confirmToDeleteExternalAccount(dispatch, type, accountId, listParams);

  const openClearBrivoUsers = (accountId) =>
    history.push(`${location.pathname}/clear-users/${accountId}`);

  if (!accountSchema) {
    return <TableSkeleton columnsLength={5} entriesLength={10} />;
  }

  const accounts = externalAccounts || {};
  const columns = generateColumns(
    accountSchema,
    getRemoveAccountAction,
    openClearBrivoUsers,
  );

  const defaultSort = TABLE_CONSTANTS.name;
  const filters = [];
  const defaultParams = {
    limit: 25,
    offset: 0,
  };
  const action = [
    {
      url: `${location.pathname}/create`,
      label: `${t('actions.create')} ${accountSchema?.name}`,
    },
  ];

  const onRowClick = (rowInfo) => {
    const id = rowInfo?.original.id;
    id && history.push(`${location.pathname}/${id}`);
  };

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  return (
    <AsyncTableContainer
      tableId={tableId}
      columns={columns}
      defaultSort={defaultSort}
      filters={filters}
      items={accounts}
      getItems={getItems}
      listKey={tableId}
      listParams={listParams}
      defaultParams={defaultParams}
      onRowClick={onRowClick}
      rowStyles={rowStyles}
      action={<ListActions actions={action} />}
      manual
    />
  );
};
